import { useQuery } from '@tanstack/react-query';
import type { Vehicle, VehicleDerivative } from '@/features/vehiclesearch';
import { checkVehicleAgreements } from '@/features/agreements';
import type { VehicleAgreementCheck } from '@/features/agreements';
import { agreementCheckIdentity } from '../utils/agreementCheckIdentity';
import type { VehicleIdentity } from '../utils/vehicleIdentity';

export type ExistingAgreementCheckState =
    | { state: 'idle' }
    | { state: 'checking' }
    | { state: 'error' }
    | { state: 'result'; check: VehicleAgreementCheck };

export const existingAgreementCheckKey = (identity: VehicleIdentity | null) =>
    ['agreements', 'vehicle-check', identity?.vin ?? '', identity?.vrm ?? ''] as const;

export function useExistingAgreementCheck(
    selectedVehicle: Vehicle | VehicleDerivative | null,
): ExistingAgreementCheckState {
    const identity = agreementCheckIdentity(selectedVehicle);

    const query = useQuery({
        queryKey: existingAgreementCheckKey(identity),
        queryFn: () => checkVehicleAgreements(identity!),
        enabled: identity !== null,
        retry: 1,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    if (identity === null) return { state: 'idle' };
    if (query.isError) return { state: 'error' };
    if (query.data) return { state: 'result', check: query.data };
    return { state: 'checking' };
}
